import styled from '@emotion/styled';
import React, { ButtonHTMLAttributes, forwardRef } from 'react';
import { colors, semantic_colors, shapes } from '../../foundation';
import { ColorPickerSize } from './types';
import { checker } from './styles';

export interface ColorPickerTriggerProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  color?: string;
  opened?: boolean;
  size?: ColorPickerSize;
  can_open_preset: boolean;
}
export const TRIGGER_SIZE = {
  xxsmall: {
    block: 14,
    padding: 2,
    borderRadius: 6,
    innerBorderRadius: 3,
  },
  xsmall: {
    block: 20,
    padding: 3,
    borderRadius: 8,
    innerBorderRadius: 4,
  },
  medium: {
    block: 30,
    padding: 3,
    borderRadius: 8,
    innerBorderRadius: 4,
  },
};

export const ColorPickerTrigger = forwardRef<HTMLButtonElement, ColorPickerTriggerProps>(
  ({ color, opened, size = 'medium', ...props }, ref) => {
    const size_value = TRIGGER_SIZE[size];
    return (
      <StyledButton
        {...props}
        opened={Boolean(opened)}
        color={color}
        disabled={Boolean(props.disabled)}
        size={size_value.block}
        borderRadius={size_value.borderRadius}
        padding={size_value.padding}
        ref={ref}
      >
        <ColorBlockBg colored={!!color} size={size}>
          <ColorBlock borderRadius={size_value.innerBorderRadius} data-testid='color-picker-trigger-color-block'>
            {!color && <NoColor data-testid='color-picker-trigger-no-color' />}
          </ColorBlock>
        </ColorBlockBg>
      </StyledButton>
    );
  },
);

const ColorBlockBg = styled.div<{ colored: boolean; size: ColorPickerSize }>`
  ${({ colored }) => (colored ? checker : `background-color: ${colors.white};`)}
  width: 100%;
  height: 100%;
  ${({ size }) => (size === 'xxsmall' ? 'border-radius: 3px' : shapes.border_radius.xsmall)};
`;

const ColorBlock = styled.div<{ borderRadius: number }>`
  position: relative;
  overflow: hidden;
  padding: 0;
  border: 0;
  border-radius: ${({ borderRadius }) => borderRadius}px;
  box-shadow: 0px 0px 0px 1px rgba(0, 0, 0, 0.1) inset;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const NoColor = styled.div`
  position: absolute;
  top: 45%;
  left: 0;
  height: 2px;
  width: 133%;
  transform-origin: 50% 50%;
  transform: translate(-13%) rotate(-45deg);
  background-color: ${semantic_colors.state.negative};
  mix-blend-mode: multiply;
`;

const StyledButton = styled.button<{
  opened: boolean;
  color?: string;
  disabled: boolean;
  size: number;
  borderRadius: number;
  padding: number;
  can_open_preset: boolean;
}>`
  background-color: ${colors.white};
  padding: ${({ padding }) => padding}px;
  box-sizing: content-box;
  width: ${({ size }) => size}px;
  height: ${({ size }) => size}px;
  flex-shrink: 0;
  border-radius: ${({ borderRadius }) => borderRadius}px;
  transition: border-color 0.1s linear, box-shadow 0.1s linear;
  border: 1px solid ${semantic_colors.border.secondary};

  ${({ disabled, opened, color, can_open_preset }) =>
    disabled
      ? `
    cursor: not-allowed;
    ${
      color
        ? `
        ${ColorBlock} {
         background-color: ${color ?? 'transparent'};
        }
      `
        : `opacity: 0.4;`
    }
  `
      : `
          cursor: pointer;
          ${ColorBlock} {
            background-color: ${color ?? 'transparent'};
          }
          ${
            can_open_preset &&
            `&:hover {
              border-color: ${semantic_colors.accent.primary};
          }`
          }
    ${
      opened &&
      `
      border-color: ${semantic_colors.accent.primary};
      box-shadow: 0px 0px 0px 2px rgba(71, 105, 247, 0.2);
    `
    }
  `};
`;
